// DELEGATE — the team-wide Evidence usage read and its transport moved to
// `@heidi/evidence`; the app-resolved auth gate is what stays.
//
// The pre-move gate was `token && enabled && teamId`. Only the first two terms
// are app facts, so only they collapse here — `teamId` is the hook's own
// argument and the second element of its cache identity, and it stays
// package-side where an empty one cannot become
// `/user-activity/team/undefined/evidence`.
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useQueryTeamEvidenceUsageStats as useQueryTeamEvidenceUsageStatsCore } from '@heidi/evidence/data';

export { TEAM_EVIDENCE_USAGE_STATS_QUERY_KEY } from '@heidi/evidence/contracts';

/**
 * GET /api/v2/ml-scribe/user-activity/team/{team_id}/evidence
 *
 * Aggregate evidence usage stats summed across every member of a team — used
 * on team-admin surfaces such as the 90-day trial recap modal so the recap
 * reflects the whole practice's engagement, not just the admin's personal
 * usage.
 *
 * The endpoint returns 403 for non-admin callers, so consumers must gate the
 * call upstream by passing `enabled: false` when the current user isn't an
 * admin of the team.
 */
export function useQueryTeamEvidenceUsageStats(
  teamId: string | undefined,
  enabled: boolean
) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQueryTeamEvidenceUsageStatsCore(teamId, isAuthenticated && enabled);
}
